import type { ActivityDuplicateRef } from "./activities";
// Import review (INT-2) — mirrors packages/api/src/lib/activity-review.ts /
// packages/web/src/api/activities.ts response shapes exactly, so web and
// mobile share one contract for GET/POST /activities/:id/review.

export type ReviewItemType = "peak" | "landmark" | "trail_section";

export interface ReviewItemRef {
  itemType: ReviewItemType;
  itemId: string;
}

export interface ReviewPeak {
  id: string;
  name: string;
  slug: string;
  elevationM: number;
  latitude: number;
  longitude: number;
  distanceM: number;
  /** Matched items only (spec 28): this activity first collected it. */
  isNew?: boolean;
  /** Matched items only: which visit this was (1 = first). 0 on near misses. */
  visitNumber?: number;
}

export interface ReviewLandmark {
  id: string;
  name: string;
  slug: string;
  category: string;
  latitude: number;
  longitude: number;
  distanceM: number;
  /** Matched items only (spec 28): this activity first collected it. */
  isNew?: boolean;
  /** Matched items only: which visit this was (1 = first). 0 on near misses. */
  visitNumber?: number;
}

export interface ReviewSection {
  id: string;
  trailId: string;
  trailName: string;
  sectionNumber: number;
  name: string | null;
  distanceKm: number | null;
  coveragePct: number;
  geojson: { type: "LineString"; coordinates: [number, number][] } | null;
}

export interface ReviewOverride {
  id: string;
  itemType: string;
  itemId: string;
  action: string;
  createdAt: string;
}

export interface ActivityReviewResponse {
  activity: {
    id: string;
    name: string;
    sourceType: string;
    sourceId: string | null;
    startedAt: string;
    durationSeconds: number | null;
    distanceM: number | null;
    elevationGainM: number | null;
    processedAt: string | null;
    geojson: { type: "LineString"; coordinates: [number, number][] } | null;
    /** The trip this activity belongs to (spec 35). Absent on older API builds. */
    trip?: { id: string; name: string } | null;
    /** Spec 37: parked duplicates of this walk from other sources. */
    duplicates?: ActivityDuplicateRef[];
  };
  matched: { peaks: ReviewPeak[]; landmarks: ReviewLandmark[]; sections: ReviewSection[] };
  nearMisses: { peaks: ReviewPeak[]; landmarks: ReviewLandmark[]; sections: ReviewSection[] };
  overrides: ReviewOverride[];
}

/**
 * Short tag for a matched peak or landmark (spec 28): "new" when this activity
 * first collected it, "3rd visit" for a repeat, nothing otherwise (an older
 * walk matched after a newer one is neither).
 */
export function visitTag(item: { isNew?: boolean; visitNumber?: number }): string | null {
  if (item.isNew) return "new";
  const n = item.visitNumber ?? 0;
  if (n < 2) return null;
  const tens = n % 100;
  const suffix =
    tens >= 11 && tens <= 13 ? "th" : n % 10 === 1 ? "st" : n % 10 === 2 ? "nd" : n % 10 === 3 ? "rd" : "th";
  return `${n}${suffix} visit`;
}
